import { YStack, XStack, Text, styled } from 'tamagui'
import { Button, NetworkFlowVisualizer } from '../ui'
import { copy } from '../../content/copy'
import {
  LOGO_ALT,
  LOGO_MARK_SRC,
  LOGO_SIZE_HERO,
} from '../../lib/logo'

const Section = styled(YStack, {
  name: 'HeroSection',
  minHeight: '100vh',
  justifyContent: 'center',
  alignItems: 'center',
  paddingHorizontal: '$6',
  paddingVertical: '$12',
  backgroundColor: '$background',
  position: 'relative',
  overflow: 'hidden',

  '$md': {
    paddingHorizontal: '$4',
    paddingVertical: '$8',
  },
})

const Content = styled(YStack, {
  name: 'HeroContent',
  maxWidth: 900,
  alignItems: 'center',
  gap: '$6',
  zIndex: 1,
})

const EyebrowText = styled(Text, {
  name: 'EyebrowText',
  fontFamily: '$accent',
  fontSize: '$3',
  letterSpacing: 3,
  color: '$orangePrimary',
  textTransform: 'uppercase',
  textAlign: 'center',
})

const Headline = styled(Text, {
  name: 'Headline',
  // @ts-ignore
  render: 'h1',
  fontFamily: '$heading',
  fontSize: '$12',
  lineHeight: '$12',
  fontWeight: '700',
  color: '$textPrimary',
  textAlign: 'center',
  letterSpacing: -2,

  '$md': {
    fontSize: '$10',
    lineHeight: '$10',
  },
  '$sm': {
    fontSize: '$8',
    lineHeight: '$8',
  },
})

const Subheadline = styled(Text, {
  name: 'Subheadline',
  fontFamily: '$body',
  fontSize: '$6',
  lineHeight: '$7',
  color: '$textSecondary',
  textAlign: 'center',
  maxWidth: 700,

  '$md': {
    fontSize: '$5',
    lineHeight: '$6',
  },
})

const CtaContainer = styled(YStack, {
  name: 'CtaContainer',
  width: '100%',
  maxWidth: 400,
  marginTop: '$4',
})

const TrustSignals = styled(YStack, {
  name: 'TrustSignals',
  alignItems: 'center',
  gap: '$3',
  marginTop: '$6',
})

const TrustText = styled(Text, {
  name: 'TrustText',
  fontFamily: '$body',
  fontSize: '$3',
  color: '$textMuted',
})

const AvailabilityBadge = styled(XStack, {
  name: 'AvailabilityBadge',
  backgroundColor: 'rgba(96, 165, 250, 0.08)',
  borderRadius: '$3',
  paddingHorizontal: '$3',
  paddingVertical: '$2',
  alignItems: 'center',
  gap: '$2',
})

const PulseDot = styled(YStack, {
  name: 'PulseDot',
  width: 8,
  height: 8,
  borderRadius: 4,
  backgroundColor: '#60A5FA',
})

interface HeroSectionProps {
  onCtaClick: () => void
}

export function HeroSection({ onCtaClick }: HeroSectionProps) {
  return (
    <Section>
      <NetworkFlowVisualizer />

      <Content>
        <img
          src={LOGO_MARK_SRC}
          alt={LOGO_ALT}
          width={LOGO_SIZE_HERO}
          height={LOGO_SIZE_HERO}
          onClick={() =>
            window.dispatchEvent(new CustomEvent('open-secure-gate'))
          }
          style={{ width: LOGO_SIZE_HERO, height: 'auto', cursor: 'pointer' }}
        />

        <EyebrowText>{copy.hero.eyebrow}</EyebrowText>

        <Headline>{copy.hero.headline}</Headline>

        <Subheadline>{copy.hero.subheadline}</Subheadline>

        <CtaContainer>
          <Button
            variant="primary"
            size="xl"
            fullWidth
            onPress={onCtaClick}
          >
            {copy.hero.cta}
          </Button>
        </CtaContainer>

        <TrustSignals>
          <TrustText>{copy.hero.trustSignal}</TrustText>
          <AvailabilityBadge>
            <PulseDot />
            <Text
              fontFamily="$body"
              fontSize="$3"
              color="#60A5FA"
              fontWeight="500"
            >
              {copy.hero.availability}
            </Text>
          </AvailabilityBadge>
        </TrustSignals>
      </Content>
    </Section>
  )
}
